import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import { API_BASE_URL } from "../layouts/constants";
import logo from "../assets/image/eblackdog_logo.png";

/* =========================================================
   ICONS
========================================================= */

const IcoBuilding = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
  </svg>
);

const IcoPhone = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const IcoMail = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const IcoPin = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IcoGuard = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IcoRoute = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="6" cy="19" r="3" />
    <circle cx="18" cy="5" r="3" />
    <path d="M12 19h4.5a3.5 3.5 0 0 0 0-7h-8a3.5 3.5 0 0 1 0-7H12" />
  </svg>
);

const IcoShield = ({ size = 14, color = "var(--accent)" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const IcoArrow = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const IcoAlert = ({ color = "var(--red)" }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const IcoPaw = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <ellipse cx="6" cy="6" rx="2.5" ry="3" />
    <ellipse cx="12" cy="4" rx="2.5" ry="3" />
    <ellipse cx="18" cy="6" rx="2.5" ry="3" />
    <ellipse cx="3.5" cy="12" rx="2" ry="2.5" />
    <path d="M12 22c-4 0-8-3-8-7 0-2 1.5-3.5 3-4.5 1-.7 2.5-1.5 5-1.5s4 .8 5 1.5c1.5 1 3 2.5 3 4.5 0 4-4 7-8 7z" />
  </svg>
);

/* =========================================================
   DEFAULT STATE
========================================================= */

const EMPTY_DASHBOARD_DATA = {
  company_name: "",
  tenant_code: "",
  licensedFor: "",
  admin_active: 0,
  company_address: "",
  company_phone: "",
  admin_email: "",
  company_logo: "",
  user_map: "",
  totalGuards: 0,
  activeRoutes: 0,
  hasIsActive: false,
};

const EMPTY_SENTINEL_DATA = {
  totalGuards: 0,
  totalRoutes: 0,
  loading: true,
  error: false,
};

/* =========================================================
   SAFE JSON PARSER
========================================================= */

function safeParseJSON(value, fallback = {}) {
  try {
    return JSON.parse(value || "");
  } catch {
    return fallback;
  }
}

/* =========================================================
   DASHBOARD COMPONENT
========================================================= */

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isGuardDashboard, setIsGuardDashboard] = useState(false);

  const [dashboardData, setDashboardData] = useState(
    EMPTY_DASHBOARD_DATA
  );

  const [sentinelData, setSentinelData] = useState(
    EMPTY_SENTINEL_DATA
  );

  /* =========================================================
     SESSION CLEAR
  ========================================================= */

  const clearCurrentSession = () => {
    const sessionKeys = [
      "token",
      "authToken",
      "guardToken",

      "isLoggedIn",
      "isGuardLoggedIn",
      "guardLoginType",

      "userData",
      "guardData",

      "company_name",
      "tenant_code",
      "licensedFor",
      "admin_active",
      "company_address",
      "company_phone",
      "admin_email",
      "company_logo",
      "user_map",

      "adminId",
      "admin_id",
      "userId",
      "role",
    ];

    sessionKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  };

  /* =========================================================
     ADMIN DASHBOARD API
  ========================================================= */

  const fetchAdminDashboard = async (token, signal) => {
    const requestUrl =
      `${API_BASE_URL}/dashboard/admin/dashboard` +
      `?timestamp=${Date.now()}`;

    console.log("[AdminDashboard] Fetching:", requestUrl);

    const response = await fetch(requestUrl, {
      method: "GET",
      signal,
      cache: "no-store",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });

    if (response.status === 401 || response.status === 403) {
      const error = new Error("Authentication failed");
      error.status = response.status;
      throw error;
    }

    if (!response.ok) {
      let errorMessage = "Failed to fetch dashboard data";

      try {
        const errorBody = await response.json();

        errorMessage =
          errorBody?.message ||
          errorBody?.error ||
          errorMessage;
      } catch {
        // Response may not contain JSON.
      }

      const error = new Error(errorMessage);
      error.status = response.status;

      throw error;
    }

    const result = await response.json();

    console.log("[AdminDashboard] API result:", result);

    return result;
  };

  /* =========================================================
     LOAD DASHBOARD
  ========================================================= */

  useEffect(() => {
    const controller = new AbortController();
    let componentActive = true;

    const loadDashboard = async () => {
      setLoading(true);

      setDashboardData(EMPTY_DASHBOARD_DATA);

      setSentinelData({
        totalGuards: 0,
        totalRoutes: 0,
        loading: true,
        error: false,
      });

      const isAdminLoggedIn =
        localStorage.getItem("isLoggedIn") === "true";

      const isGuardLoggedIn =
        localStorage.getItem("isGuardLoggedIn") === "true";

      /*
        Important:
        Admin uses only authToken.
        Guard uses only guardToken.

        Do not use:
        localStorage.getItem("token") || ...
        because an old token can be selected.
      */
      const adminToken = localStorage.getItem("authToken");
      const guardToken = localStorage.getItem("guardToken");

      console.log("[AdminDashboard] Session check:", {
        isAdminLoggedIn,
        isGuardLoggedIn,
        hasAdminToken: Boolean(adminToken),
        hasGuardToken: Boolean(guardToken),
      });

      /*
        GUARD LOGIN
      */
      if (isGuardLoggedIn) {
        if (!guardToken) {
          clearCurrentSession();
          navigate("/", { replace: true });
          return;
        }

        const guardData = safeParseJSON(
          localStorage.getItem("guardData"),
          {}
        );

        const guardLoginType =
          localStorage.getItem("guardLoginType") || "";

        const guardName =
          guardData.fullName ||
          guardData.full_name ||
          guardData.name ||
          guardData.guardName ||
          guardData.fld_name ||
          localStorage.getItem("guardName") ||
          "Guard";

        const guardEmail =
          guardData.email ||
          guardData.fld_mail ||
          localStorage.getItem("guardEmail") ||
          "";

        const guardPhone =
          guardData.phone ||
          guardData.mobile ||
          guardData.fld_mobile ||
          localStorage.getItem("guardPhone") ||
          "";

        const guardAddress =
          guardData.address ||
          guardData.fld_address ||
          localStorage.getItem("guardAddress") ||
          "";

        if (!componentActive) {
          return;
        }

        setIsGuardDashboard(true);

        setDashboardData({
          company_name: guardName,
          tenant_code: "",
          licensedFor:
            guardLoginType === "patrol"
              ? "Patrol Guard Dashboard"
              : "Sentinel Guard Dashboard",
          admin_active: 1,
          company_address: guardAddress,
          company_phone: guardPhone,
          admin_email: guardEmail,
          company_logo: "",
          user_map: localStorage.getItem("user_map") || "",
          totalGuards: 0,
          activeRoutes: 0,
          hasIsActive: false,
        });

        setSentinelData({
          totalGuards: 0,
          totalRoutes: 0,
          loading: false,
          error: false,
        });

        setLoading(false);

        return;
      }

      /*
        ADMIN LOGIN
      */
      if (!isAdminLoggedIn || !adminToken) {
        clearCurrentSession();
        navigate("/", { replace: true });
        return;
      }

      setIsGuardDashboard(false);

      try {
        const result = await fetchAdminDashboard(
          adminToken,
          controller.signal
        );

        if (!componentActive) {
          return;
        }

        const apiData = result?.data || {};
        const companyDetails = apiData?.companyDetails || {};

        const newDashboardData = {
          company_name:
            companyDetails.company_name ||
            companyDetails.companyName ||
            "",

          tenant_code:
            companyDetails.tenant_code ||
            companyDetails.tenantCode ||
            "",

          licensedFor:
            companyDetails.licensedFor ||
            companyDetails.licensed_for ||
            companyDetails.num_licenses ||
            "",

          admin_active: Number(
            companyDetails.admin_active ??
              companyDetails.is_active ??
              0
          ),

          company_address:
            companyDetails.company_address ||
            companyDetails.address ||
            "",

          company_phone:
            companyDetails.company_phone ||
            companyDetails.phone ||
            "",

          admin_email:
            companyDetails.admin_email ||
            companyDetails.email ||
            "",

          company_logo:
            companyDetails.company_logo ||
            companyDetails.logo_path ||
            "",

          user_map:
            companyDetails.user_map || "",

          totalGuards: Number(
            apiData.totalGuards ?? 0
          ),

          activeRoutes: Number(
            apiData.totalRoutes ?? 0
          ),

          hasIsActive:
            companyDetails.is_active !== undefined ||
            companyDetails.admin_active !== undefined,
        };

        const newSentinelData = {
          totalGuards: Number(
            apiData.sentinelGuards ?? 0
          ),

          totalRoutes: Number(
            apiData.sentinelRoutes ?? 0
          ),

          loading: false,
          error: false,
        };

        console.log(
          "[AdminDashboard] Dashboard state:",
          newDashboardData
        );

        console.log(
          "[AdminDashboard] Sentinel state:",
          newSentinelData
        );

        setDashboardData(newDashboardData);
        setSentinelData(newSentinelData);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.error(
          "[AdminDashboard] Dashboard fetch error:",
          error
        );

        if (
          error.status === 401 ||
          error.status === 403
        ) {
          clearCurrentSession();
          navigate("/", { replace: true });
          return;
        }

        if (!componentActive) {
          return;
        }

        setDashboardData(EMPTY_DASHBOARD_DATA);

        setSentinelData({
          totalGuards: 0,
          totalRoutes: 0,
          loading: false,
          error: true,
        });
      } finally {
        if (componentActive) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      componentActive = false;
      controller.abort();
    };
  }, [navigate]);

  /* =========================================================
     ROUTE PAGE
  ========================================================= */

  const routePage = useMemo(() => {
    return dashboardData.user_map === "google"
      ? "/route-master"
      : "/route-master1";
  }, [dashboardData.user_map]);

  const isActive =
    Number(dashboardData.admin_active) === 1;

  /* =========================================================
     LOADING UI
  ========================================================= */

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />

        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading dashboard...
        </p>
      </div>
    );
  }

  /* =========================================================
     DASHBOARD UI
  ========================================================= */

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="dashboard"
      activeMenu="dashboard"
    >
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          {isGuardDashboard
            ? "Guard Dashboard"
            : "Company License Management"}
        </h1>

        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          {isGuardDashboard
            ? "Guard access dashboard & operational details"
            : "Licensed company information & operational details"}
        </p>

        {/* COMPANY INFORMATION CARD */}

        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 sm:p-6 shadow-[0_8px_32px_rgba(0,0,0,0.35)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />

          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="w-20 h-20 rounded-xl bg-[var(--page-bg)] border-2 border-[var(--accent)] shadow-[0_0_0_3px_var(--accent-glow)] flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                src={logo}
                alt="Company Logo"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="flex-1 text-center sm:text-left min-w-[200px]">
              <p className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
                {dashboardData.company_name || "Company Name"}

                {!isGuardDashboard &&
                dashboardData.tenant_code
                  ? ` (${dashboardData.tenant_code})`
                  : ""}
              </p>

              <p className="text-[12.5px] text-[var(--text-muted)] m-0 mb-2.5">
                {dashboardData.licensedFor ||
                  "Licensed Service"}
              </p>

              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11.5px] font-bold ${
                  isActive
                    ? "bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)]"
                    : "bg-[var(--red-faint)] border border-[var(--red-border)] text-[var(--red)]"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    isActive
                      ? "bg-[var(--accent)]"
                      : "bg-[var(--red)]"
                  }`}
                />

                {isActive ? "Active" : "Inactive"}
              </span>
            </div>

            <div className="hidden sm:flex items-center gap-1.5 bg-[var(--accent-faint)] border border-[var(--accent-border)] px-3 py-1 rounded-full flex-shrink-0">
              <IcoPaw />

              <span className="text-[10.5px] font-bold tracking-wide uppercase text-[var(--accent)]">
                eBLACKDOG System
              </span>
            </div>
          </div>
        </div>

        {/* CONTACT AND STATISTICS */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-[18px]">
          {/* CONTACT INFORMATION */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 sm:p-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <div className="flex items-center gap-2 mb-4">
              <IcoBuilding />

              <p className="text-[12px] font-bold tracking-wide uppercase text-[var(--text-sub)] m-0">
                Contact Information
              </p>

              <div className="flex-1 h-px bg-[var(--divider)]" />
            </div>

            {[
              {
                label: "Address",
                icon: <IcoPin />,
                value: dashboardData.company_address,
              },
              {
                label: "Phone",
                icon: <IcoPhone />,
                value: dashboardData.company_phone,
              },
              {
                label: "Email",
                icon: <IcoMail />,
                value: dashboardData.admin_email,
              },
            ].map(({ label, icon, value }) => (
              <div
                key={label}
                className="flex items-start gap-3 py-3 border-b border-[var(--divider)] last:border-b-0 last:pb-0"
              >
                <div className="w-[34px] h-[34px] rounded-xl bg-[var(--accent-faint)] border border-[var(--accent-border)] flex items-center justify-center text-[var(--accent)] flex-shrink-0">
                  {icon}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold tracking-wide uppercase text-[var(--text-faint)] m-0 mb-0.5">
                    {label}
                  </p>

                  <p className="text-[13.5px] font-medium text-[var(--text-primary)] m-0 break-words">
                    {value || "—"}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* OPERATIONAL STATISTICS */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 sm:p-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <div className="flex items-center gap-2 mb-4">
              <IcoShield
                size={13}
                color="var(--accent)"
              />

              <p className="text-[12px] font-bold tracking-wide uppercase text-[var(--text-sub)] m-0">
                Operational Statistics
              </p>

              <div className="flex-1 h-px bg-[var(--divider)]" />
            </div>

            {!isGuardDashboard && (
              <>
                <p className="text-[10px] font-bold tracking-wide uppercase text-[var(--text-faint)] m-0 mb-2.5">
                  Main Flow
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {/* TOTAL GUARDS */}

                  {/* TOTAL GUARDS */}

<div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
  <div className="absolute right-[-10px] top-[-10px] w-14 h-14 rounded-full bg-[var(--accent-faint)]" />

  <div className="flex items-center gap-1.5 mb-1.5">
    <IcoGuard />

    <p className="text-[10px] font-bold tracking-wide uppercase text-[var(--text-faint)] m-0">
      Total Guards
    </p>
  </div>

  <p className="text-[34px] font-extrabold leading-none m-0 text-[var(--accent)]">
    {dashboardData.totalGuards}
  </p>
</div>

                  {/* TOTAL ROUTES */}

                  <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
                    <div className="absolute right-[-10px] top-[-10px] w-14 h-14 rounded-full bg-[var(--accent-faint)]" />

                    <div className="flex items-center gap-1.5 mb-1.5">
                      <IcoRoute />

                      <p className="text-[10px] font-bold tracking-wide uppercase text-[var(--text-faint)] m-0">
                        Total Routes
                      </p>
                    </div>

                    <p className="text-[34px] font-extrabold leading-none m-0 text-[var(--accent)]">
                      {dashboardData.activeRoutes}
                    </p>
                  </div>
                </div>

                {/* SENTINEL SEPARATOR */}

                <div className="flex items-center gap-2.5 my-4">
                  <div className="flex-1 h-px bg-[var(--divider)]" />

                  <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--accent-faint)] border border-[var(--accent-border)]">
                    <IcoShield
                      size={11}
                      color="var(--accent)"
                    />

                    <span className="text-[10px] font-bold tracking-wide uppercase text-[var(--accent)]">
                      Sentinel Flow
                    </span>
                  </div>

                  <div className="flex-1 h-px bg-[var(--divider)]" />
                </div>

                {/* SENTINEL DATA */}

                {sentinelData.loading ? (
                  <div className="flex items-center gap-2.5 py-3.5">
                    <div className="w-[18px] h-[18px] border-2.5 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />

                    <span className="text-[13px] text-[var(--text-muted)]">
                      Loading sentinel data...
                    </span>
                  </div>
                ) : sentinelData.error ? (
                  <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[var(--red-faint)] border border-[var(--red-border)] text-[13px] text-[var(--red)]">
                    <IcoAlert />

                    <span>
                      Unable to load sentinel data
                    </span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {/* SENTINEL GUARDS */}

                    <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--accent-faint)] border border-[var(--accent-border)]">
                      <div className="absolute right-[-10px] top-[-10px] w-14 h-14 rounded-full bg-[var(--accent-glow)]" />

                      <div className="flex items-center gap-1.5 mb-1.5">
                        <IcoShield
                          size={13}
                          color="var(--accent)"
                        />

                        <p className="text-[10px] font-bold tracking-wide uppercase text-[var(--accent)] m-0">
                          Sentinel Guards
                        </p>
                      </div>

                      <p className="text-[34px] font-extrabold leading-none m-0 text-[var(--text-primary)]">
                        {sentinelData.totalGuards}
                      </p>
                    </div>

                    {/* SENTINEL ROUTES */}

                    <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--accent-faint)] border border-[var(--accent-border)]">
                      <div className="absolute right-[-10px] top-[-10px] w-14 h-14 rounded-full bg-[var(--accent-glow)]" />

                      <div className="flex items-center gap-1.5 mb-1.5">
                        <IcoShield
                          size={13}
                          color="var(--accent)"
                        />

                        <p className="text-[10px] font-bold tracking-wide uppercase text-[var(--accent)] m-0">
                          Sentinel Routes
                        </p>
                      </div>

                      <p className="text-[34px] font-extrabold leading-none m-0 text-[var(--text-primary)]">
                        {sentinelData.totalRoutes}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* GUARD DASHBOARD MESSAGE */}

            {isGuardDashboard && (
              <div className="rounded-xl p-4 bg-[var(--accent-faint)] border border-[var(--accent-border)]">
                <div className="flex items-center gap-2 mb-2">
                  <IcoShield
                    size={15}
                    color="var(--accent)"
                  />

                  <p className="text-[13px] font-bold text-[var(--accent)] m-0">
                    Guard Login Active
                  </p>
                </div>

                <p className="text-[13px] text-[var(--text-muted)] m-0">
                  You are logged in as a guard.
                  Admin-only dashboard API calls are skipped
                  for this session.
                </p>
              </div>
            )}

            {/* QUICK LINKS */}

            {/* QUICK LINKS */}

<div className="flex flex-wrap gap-2.5 mt-4 pt-4 border-t border-[var(--divider)]">
  {!isGuardDashboard &&
    [
      {
        label: "View Users",
        to: "/user_list",
      },
      {
        label: "Route List",
        to: "/route_list",
      },
      {
        label: "Analytics",
        to: "/analytics",
      },
    ].map(({ label, to }) => (
      <Link
        key={label}
        to={to}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)] text-[12.5px] font-semibold no-underline transition-all hover:bg-white hover:text-black hover:border-white hover:shadow-[0_4px_14px_var(--accent-glow)]"
      >
        {label}

        <IcoArrow />
      </Link>
    ))}
</div>
          </div>
        </div>
      </div>
    </Layout>
  );
}